/**
 * Sharpness of a frame: variance of a 3x3 Laplacian over a grayscale
 * copy scaled to `width` px wide. Higher is sharper; motion blur and
 * missed focus collapse it toward zero.
 *
 * One implementation feeds both the per-frame sharpness gate in
 * useFaceDetection and the `blur_var` stamped on face_captured, so the
 * threshold the gate enforces is measured in exactly the units the
 * telemetry reports. Production capture data (2026-09-16, face-api arm,
 * 806 captures) at width 160:
 *
 *   blur < 150   liveness fail 45% iOS / 20% Android
 *   blur 150-300               19% / 18%
 *   blur > 800                  1.4% / 5.8%
 *
 * Cheap (~45k pixels at 160 wide) and non-fatal: any failure returns null.
 */
let _canvas = null;
let _ctx = null;

export function measureSharpness(source, width = 160) {
  try {
    const sw = source.videoWidth || source.width;
    const sh = source.videoHeight || source.height;
    if (!sw || !sh) return null;
    const w = width;
    const h = Math.max(3, Math.round((sh / sw) * w));
    if (!_canvas) {
      _canvas = document.createElement("canvas");
      _ctx = _canvas.getContext("2d", { willReadFrequently: true });
    }
    if (_canvas.width !== w) _canvas.width = w;
    if (_canvas.height !== h) _canvas.height = h;
    _ctx.drawImage(source, 0, 0, w, h);
    const d = _ctx.getImageData(0, 0, w, h).data;
    const g = new Float32Array(w * h);
    for (let i = 0, p = 0; i < d.length; i += 4, p++) {
      g[p] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    }
    let sum = 0;
    let sumSq = 0;
    let n = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        const lap = 4 * g[i] - g[i - 1] - g[i + 1] - g[i - w] - g[i + w];
        sum += lap;
        sumSq += lap * lap;
        n++;
      }
    }
    if (!n) return null;
    const mean = sum / n;
    return Math.round(sumSq / n - mean * mean);
  } catch {
    return null;
  }
}
